import type { InboxItem } from '../features/chat/types';
import type { ParticipantItem } from '../features/chat/types/Participant';

export type ConversationIcon = 'single' | 'multiple';

export interface Conversation {
  id: string;
  name: string;
  /** Non-empty only when the chatroom has an explicit group name (e.g. "Care Gp") */
  chatroomName: string;
  subtitle: string;
  time: string;
  showExternal: boolean;
  avatarLabel: string;
  secondAvatarLabel: string;
  unreadCount: number;
  unread: boolean;
  pinnedStatus: boolean;
  icon: ConversationIcon;
  isArchived: boolean;
  /** Facility GUID associated with this chatroom — used for Add Participant */
  externalSiteId: string | null;
}

// ── Name helpers ────────────────────────────────────────────────────────────

/** Extract first name: "Jane Smith" → "Jane" */
export function extractFirstName(fullName: string): string {
  if (!fullName) return '';
  return fullName.trim().split(/\s+/)[0];
}

/**
 * Derive 2-letter initials from any display name.
 * "Smith, John" → "SJ" | "Care Gp" → "CG" | "" → ""
 */
export function toAvatarLabel(name: string): string {
  return name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ── Title helper (mirrors mobile getTeamsChatTitle) ─────────────────────────

/**
 * Returns the display title for a chatroom:
 * 1. chatroomName if non-empty (e.g. "Care Gp", "Java")
 * 2. Participant displayNames joined, excluding the current user
 * 3. Fallback to displayInfo1
 */
export function getTeamsChatTitle(item: InboxItem, currentUserName: string): string {
  if (item.chatroomName?.trim()) return item.chatroomName.trim();

  if (item.chatParticipants?.length) {
    const allNames = item.chatParticipants.map((p) => p.displayName).filter(Boolean);
    const filtered = currentUserName
      ? allNames.filter((n) => n.trim().toLowerCase() !== currentUserName.trim().toLowerCase())
      : allNames;
    const toShow = filtered.length > 0 ? filtered : allNames;
    if (toShow.length > 0) return toShow.join(', ');
  }

  return item.displayInfo1 || '';
}

// ── Subtitle helper (mirrors mobile getParticipantsList) ────────────────────

/**
 * Build participant subtitle: "John, Jane +2"
 * - Excludes the current user
 * - Uses first names
 * - Respects char budget (default 24)
 */
export function getParticipantsList(
  item: InboxItem,
  currentUserName: string,
  charBudget = 24,
): string {
  if (!item.chatParticipants?.length) return item.displayInfo1 || '';

  const allNames = item.chatParticipants.map((p) => p.displayName).filter(Boolean);
  if (!allNames.length) return item.displayInfo1 || '';

  const filtered = currentUserName
    ? allNames.filter((n) => n.trim().toLowerCase() !== currentUserName.trim().toLowerCase())
    : allNames;
  const namesToShow = filtered.length > 0 ? filtered : allNames;

  const displayNames = namesToShow
    .map((n) => { const first = extractFirstName(n); return first || n; })
    .filter(Boolean);

  if (!displayNames.length) return '';

  let totalCount = item.chatParticipantCount ?? displayNames.length;
  if (currentUserName && filtered.length < allNames.length && item.chatParticipantCount) {
    totalCount = item.chatParticipantCount - 1;
  }

  if (displayNames.length === 1 || totalCount === 1) return displayNames[0];

  const twoNames = `${displayNames[0]}, ${displayNames[1]}`;
  const remaining = totalCount - 2;

  if (remaining > 0) {
    const withCount = `${twoNames} +${remaining}`;
    if (withCount.length <= charBudget) return withCount;
    const oneWithCount = `${displayNames[0]} +${totalCount - 1}`;
    if (oneWithCount.length <= charBudget) return oneWithCount;
    return displayNames[0];
  }

  return twoNames.length <= charBudget ? twoNames : displayNames[0];
}

// ── Date helper (mirrors mobile formatDate) ─────────────────────────────────

/**
 * Format ISO date → "Today" | "Yesterday" | "3 days ago" | "2 months ago"
 */
export function formatDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return isoDate;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const targetStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (targetStart.getTime() === todayStart.getTime()) return 'Today';
  if (targetStart.getTime() === yesterdayStart.getTime()) return 'Yesterday';

  const diffDays = Math.round((todayStart.getTime() - targetStart.getTime()) / 86_400_000);
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;

  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
}

// ── Avatar for second participant (group chats) ──────────────────────────────

/**
 * For group chats (icon === "multiple"), return initials for the second participant
 * that is NOT the current user.
 */
function getSecondAvatarLabel(item: InboxItem, currentUserName: string): string {
  if (!item.chatParticipants?.length) return '';
  const others = item.chatParticipants
    .map((p) => p.displayName)
    .filter((n) => Boolean(n) && n.trim().toLowerCase() !== currentUserName.trim().toLowerCase());
  return others.length >= 2 ? toAvatarLabel(others[1]) : '';
}

// ── Main mapper ──────────────────────────────────────────────────────────────

/** Convert a raw InboxItem → Conversation shape used by the UI */
export function inboxItemToConversation(item: InboxItem, currentUserName: string): Conversation {
  const icon: ConversationIcon = item.icon === 'multiple' ? 'multiple' : 'single';
  const name = getTeamsChatTitle(item, currentUserName);
  return {
    id: String(item.chatroomId),
    name,
    chatroomName: item.chatroomName?.trim() ?? '',
    subtitle: item.displayInfo3 || '',  // last message preview
    time: formatDate(item.sentTime),
    showExternal: item.external,
    avatarLabel: toAvatarLabel(name),
    secondAvatarLabel: icon === 'multiple' ? getSecondAvatarLabel(item, currentUserName) : '',
    unreadCount: item.unreadCount ?? 0,
    unread: item.unread ?? false,
    pinnedStatus: item.pinnedStatus ?? false,
    icon,
    isArchived: item.isArchived ?? false,
    externalSiteId: item.externalSiteId ?? null,
  };
}

// ── Current-user resolver ────────────────────────────────────────────────────

/**
 * Derive the logged-in user's display name by matching externalUserId
 * across any available inbox items. Used to exclude self from title/subtitle.
 */
export function resolveCurrentUserName(items: InboxItem[], externalUserId: string): string {
  for (const item of items) {
    const match = item.chatParticipants?.find((p) => p.externalUserId === externalUserId);
    if (match?.displayName) return match.displayName;
  }
  return '';
}

// ── Search filter ────────────────────────────────────────────────────────────

export function filterBySearch(list: Conversation[], query: string): Conversation[] {
  if (!query) return list;
  const lower = query.toLowerCase();
  return list.filter(
    (c) =>
      c.name.toLowerCase().includes(lower) ||
      c.subtitle.toLowerCase().includes(lower),
  );
}

// ── Chat header participant summary ─────────────────────────────────────────

/**
 * Build a compact participant name string for the ChatHeader from ParticipantItem[].
 * Excludes the current user by userId. Shows full display names with +N for overflow.
 * Designed for a ~2-line header (charBudget 45).
 *
 * Examples: "Alice Smith, Bob Jones +3"  |  "Care Gp"  |  "Alice, Bob"
 */
export function buildParticipantSummary(
  participants: ParticipantItem[],
  currentUserId: number,
  charBudget = 45,
): string {
  if (!participants.length) return '';

  const filtered = participants
    .filter((p) => p.userId !== currentUserId)
    .map((p) => p.displayName)
    .filter(Boolean);

  const names = filtered.length > 0 ? filtered : participants.map((p) => p.displayName).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];

  // Greedily add names, always checking that the joined string + final "+N" suffix fits.
  const parts: string[] = [];

  for (let i = 0; i < names.length; i++) {
    const overflow = names.length - i - 1;
    // Would showing names[i] (with a "+overflow" suffix if needed) still fit?
    const candidate =
      overflow > 0
        ? [...parts, names[i]].join(', ') + ` +${overflow}`
        : [...parts, names[i]].join(', ');

    if (candidate.length <= charBudget) {
      parts.push(names[i]);
    } else {
      // names[i] doesn’t fit — stop here and keep the "+N" for remaining unshown names
      break;
    }
  }

  const overflow = names.length - parts.length;
  const result = parts.join(', ');
  return overflow > 0 ? `${result} +${overflow}` : result;
}

